import { cancelB2bOrder, type B2bOrderDetail, confirmB2bPiOnBehalf, getB2bOrderFileUrl, getB2bPackingListUrl } from '@api/b2b';
import { Button, FileInput, Modal, Textarea } from '@mantine/core';
import { FileXlsIcon } from '@phosphor-icons/react';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import React, { useState } from 'react';

import styles from './b2b.module.css';
import { Card, Field, OrderSteps, QuoteTotals } from './shared';
import { errorMessage, formatDay, formatMoment, openSignedFile, splitSavedLines } from './utils';

const NEXT_STEP: Record<string, string> = {
    Submitted: 'Check the order and issue the proforma invoice. You see the PI before it goes out.',
    'PI Issued': 'PI sent. Waiting for the customer to confirm it on the portal. If they confirmed by WhatsApp, email or phone, mark it confirmed here.',
    'PI Confirmed': 'Waiting for the customer to pay and upload the proof.',
    'Payment Submitted': 'A payment proof is waiting for accounts to verify.',
    'Payment Done': 'Paid. On the Dispatch tab, create an STN, then mark it dispatched once the goods leave.',
    Shipped: 'Everything has left. Waiting for the customer to confirm receipt.',
    Fulfilled: 'Received by the customer. Nothing left to do.',
    Cancelled: 'Cancelled.',
};

/** The brand's rail on a B2B order: what happens next, the PI totals, the status history. */
const NextStepCard: React.FC<{ detail: B2bOrderDetail; isAdmin: boolean; customerName: string | null | undefined; onChanged: () => void; onReview: () => void }> = ({
    detail,
    isAdmin,
    customerName,
    onChanged,
    onReview,
}) => {
    const { order, payments } = detail;
    /** One packing list per recorded dispatch (each STN is one shipment). */
    const shipped = detail.dispatches.dispatches.filter((d) => d.dispatchDetails && d.status !== 'CANCELLED');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState(false);
    const [reason, setReason] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [confirmNote, setConfirmNote] = useState('');
    const [confirmFile, setConfirmFile] = useState<File | null>(null);
    const terminal = ['Shipped', 'Fulfilled', 'Cancelled'].includes(order.status);
    // The newest PI revision on file (pi-rev{n}.pdf), if one has been issued.
    const latestPiKey = detail.files
        .map((key) => ({ key, n: Number(/\/pi-rev(\d+)\.pdf$/.exec(key)?.[1] ?? 0) }))
        .sort((a, b) => b.n - a.n)
        .find((f) => f.n > 0)?.key;
    const overdue = payments.payments.some((p) => p.overdue);
    const pi = {
        charges: order.additionalCharges ?? [],
        totals: { subtotal: order.subtotal, taxAmount: order.taxAmount, total: order.total, payable: payments.payable },
        ...splitSavedLines(order.items),
    };

    const run = async (action: () => Promise<unknown>) => {
        setBusy(true);
        setError(null);
        try {
            await action();
            onChanged();
            return true;
        } catch (e) {
            setError(errorMessage(e));
            return false;
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <Card title="Next step" hint={NEXT_STEP[order.status]}>
                <OrderSteps status={order.status} />
                {overdue ? <span className={styles.error}>A payment proof is past its verification deadline.</span> : null}
                <div className={styles.row}>
                    {order.status === 'Submitted' ? (
                        <Button size="xs" variant={ButtonVariant.ACTION} onClick={onReview}>
                            Review &amp; issue PI
                        </Button>
                    ) : null}
                    {order.status === 'PI Issued' && isAdmin ? (
                        <Button size="xs" variant={ButtonVariant.ACTION} onClick={() => setConfirming(true)}>
                            Mark PI confirmed
                        </Button>
                    ) : null}
                    {latestPiKey ? (
                        <Button size="xs" variant={ButtonVariant.DEFAULT} onClick={() => void run(() => openSignedFile(() => getB2bOrderFileUrl(order.purchaseOrderId, latestPiKey)))}>
                            View PI
                        </Button>
                    ) : null}
                    {shipped.map((d) => (
                        <Button key={d.id} size="xs" variant={ButtonVariant.DEFAULT} leftSection={<FileXlsIcon size={14} />} onClick={() => void run(() => openSignedFile(() => getB2bPackingListUrl(order.purchaseOrderId, d.id)))}>
                            {shipped.length > 1 ? `Packing list · ${d.code}` : 'Packing list'}
                        </Button>
                    ))}
                    {!terminal ? (
                        <Button size="xs" variant={ButtonVariant.OUTLINED_DESTRUCTIVE} onClick={() => setCancelling(true)}>
                            Cancel order
                        </Button>
                    ) : null}
                </div>
                {error ? <span className={styles.error}>{error}</span> : null}
            </Card>
            <Card title="Proforma invoice">
                <QuoteTotals quote={pi} />
                <div className={styles.fieldGrid}>
                    <Field label="Customer" value={customerName ?? order.b2bCustomerId} />
                    <Field label="Their PO" value={order.poRefNum ?? '—'} />
                    <Field label="Ship to" value={order.shipToName ?? '—'} />
                    <Field label="PI valid until" value={order.expiryDate ? formatDay(order.expiryDate) : '—'} />
                </div>
            </Card>
            {detail.history.length ? (
                <Card title="Status history">
                    <div className={styles.stack}>
                        {detail.history.map((h) => (
                            <div key={`${h.status}:${h.at}`} className={styles.between}>
                                <span className={styles.fieldValue}>{h.status}</span>
                                <span className={styles.muted}>{formatMoment(h.at)}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            ) : null}
            <Modal opened={confirming} onClose={() => setConfirming(false)} title={`Mark the PI for ${order.purchaseOrderId} confirmed`} centered>
                <div className={styles.stack}>
                    <span className={styles.cardHint}>
                        For when {customerName ?? 'the customer'} confirmed outside the portal. It has the same effect as their Confirm PI: the payment schedule is set from
                        their terms and they can pay.
                    </span>
                    <Textarea
                        label="How did they confirm?"
                        placeholder="e.g. WhatsApp from Ramesh, 9 Oct"
                        required
                        autosize
                        minRows={2}
                        value={confirmNote}
                        onChange={(e) => setConfirmNote(e.currentTarget.value)}
                    />
                    <FileInput
                        label="Screenshot or email (optional)"
                        placeholder="PDF, JPG or PNG, up to 10 MB"
                        accept="application/pdf,image/jpeg,image/png,image/webp"
                        value={confirmFile}
                        onChange={setConfirmFile}
                        clearable
                    />
                    {error ? <span className={styles.error}>{error}</span> : null}
                    <div className={styles.rowEnd}>
                        <Button variant={ButtonVariant.DEFAULT} disabled={busy} onClick={() => setConfirming(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant={ButtonVariant.ACTION}
                            disabled={!confirmNote.trim()}
                            loading={busy}
                            onClick={() =>
                                void run(() => confirmB2bPiOnBehalf(order.purchaseOrderId, confirmNote.trim(), confirmFile)).then((ok) => {
                                    if (!ok) return;
                                    setConfirming(false);
                                    setConfirmNote('');
                                    setConfirmFile(null);
                                })
                            }
                        >
                            Mark PI confirmed
                        </Button>
                    </div>
                </div>
            </Modal>
            <Modal opened={cancelling} onClose={() => setCancelling(false)} title={`Cancel ${order.purchaseOrderId}`} centered>
                <div className={styles.stack}>
                    <Textarea label="Reason" autosize minRows={2} value={reason} onChange={(e) => setReason(e.currentTarget.value)} />
                    <Button
                        variant={ButtonVariant.FILLED_DESTRUCTIVE}
                        disabled={!reason.trim()}
                        loading={busy}
                        onClick={() => void run(() => cancelB2bOrder(order.purchaseOrderId, reason.trim())).then((ok) => ok && setCancelling(false))}
                    >
                        Cancel order
                    </Button>
                </div>
            </Modal>
        </>
    );
};

export default NextStepCard;
